import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isDeepEqual } from 'remeda';
import {
  CustomView,
  CustomViewAttribute,
  CustomViewEntity,
  CustomViewFormFields,
  CustomViewFormModel,
  CustomViewSorting,
  filterValidCustomViewFilters,
  mapConditionsForApi,
  useCustomViewEntityConfig,
} from 'src/graphql/entities/custom-view';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import useCustomViewsStore from 'src/store/custom-views';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

interface UseConversationsFormProps {
  customView?: Partial<Pick<CustomView, 'formConditions' | 'columns' | 'sort'>>;
  id: string;
  entity: CustomViewEntity;
}

// this hook is a first iteration of refactoring, will be refactored further soon
export const useCustomViewState = ({ customView, id, entity }: UseConversationsFormProps) => {
  const [isQueryBuilderOpen, setIsQueryBuilderOpen] = useState(false);

  const {
    filtersMap,
    columnsMap,
    sortMap,
    defaultPartnerFilter,
    setDefaultPartnerFilter,
    updateFilters,
    updateColumns,
    updateSort,
  } = useCustomViewsStore();

  const { user } = useCurrentUserProfile();

  const { filterAttributesMap, attributesMap, resolveQueryParams } =
    useCustomViewEntityConfig(entity);

  const formMethods = useForm<Pick<CustomViewFormModel, CustomViewFormFields.Conditions>>({
    defaultValues: {
      [CustomViewFormFields.Conditions]: customView?.formConditions ?? {
        rules: [],
        combinator: 'and',
        not: false,
      },
    },
  });

  const rawFilters = useMemo(() => {
    if (!filtersMap[id]) {
      return undefined;
    }

    return {
      ...filtersMap[id],
      rules: filtersMap[id].rules.map((rule) => ({
        ...rule,
        field: filterAttributesMap.get(rule.field!.value),
      })),
    };
  }, [filtersMap, id, filterAttributesMap]);

  const rawColumns = useMemo(() => {
    if (!columnsMap[id]?.length) {
      return undefined;
    }

    return columnsMap[id]
      .filter((field) => attributesMap.has(field.attribute))
      .map((field) => attributesMap.get(field.attribute) || field);
  }, [columnsMap, id, attributesMap]);

  const filters = rawFilters ?? customView?.formConditions;
  const unresolvedColumns = rawColumns ?? customView?.columns;
  const columns = useMemo(
    () => unresolvedColumns?.filter((col) => attributesMap.has(col.attribute)),
    [unresolvedColumns, attributesMap],
  );
  const sort = sortMap[id] ?? customView?.sort;

  const handleFiltersUpdate = useCallback(
    (filters: CustomViewFormModel['conditions'] | null) => {
      updateFilters(
        id,
        filters
          ? {
              ...filters,
              rules: getConditionIdentifiersOnly(
                filters,
              ) as CustomViewFormModel['conditions']['rules'],
            }
          : null,
      );
    },
    [id, updateFilters],
  );

  const handleColumnsUpdate = useCallback(
    (columns: CustomViewAttribute[] | null) => {
      updateColumns(id, columns?.map((col, order) => ({ ...col, order })) ?? null);
    },
    [id, updateColumns],
  );

  const handleSortChange = useCallback(
    (sort: CustomViewSorting | null) => {
      updateSort(id, sort);
    },
    [id, updateSort],
  );

  const formConditions = useMemo<CustomViewFormModel['conditions']>(() => {
    return (
      filters ??
      customView?.formConditions ?? {
        rules: [],
        combinator: 'and',
        not: false,
      }
    );
  }, [filters, customView]);

  const rawQuery = useMemo(
    () => ({
      conditions: mapConditionsForApi(formConditions, { user, interpolateValues: true }),
      params: resolveQueryParams(formConditions.rules),
    }),
    [formConditions, user, resolveQueryParams],
  );

  const { conditions: mappedConditions, params: queryParams } = useDebouncedValue(
    rawQuery,
    500,
  );

  useEffect(() => {
    if (!isQueryBuilderOpen) {
      formMethods.reset({
        [CustomViewFormFields.Conditions]: formConditions,
      });
    }
  }, [isQueryBuilderOpen, formConditions, formMethods]);

  const handleColumnUpdate = useCallback(
    (column: CustomViewAttribute) => {
      const initialColumns = columns ?? customView?.columns ?? [];

      if (!column) return;

      const isDeleteOperation = initialColumns.some((c) => c.attribute === column.attribute);

      if (isDeleteOperation) {
        updateColumns(
          id,
          initialColumns.filter((c) => c.attribute !== column.attribute),
        );
      } else {
        updateColumns(id, [
          ...initialColumns,
          { ...column, order: initialColumns.length + 1 },
        ]);
      }
    },
    [id, columns, customView?.columns, updateColumns],
  );

  const toggleQueryBuilder = useCallback(() => {
    if (isQueryBuilderOpen && filters?.rules?.length) {
      const sanitizedRules = filterValidCustomViewFilters(filters.rules);

      handleFiltersUpdate({
        ...filters,
        rules: sanitizedRules,
      });
    }

    setIsQueryBuilderOpen((isOpen) => !isOpen);
  }, [handleFiltersUpdate, isQueryBuilderOpen, filters, setIsQueryBuilderOpen]);

  const resetToDefault = useCallback(() => {
    handleFiltersUpdate(null);
    handleColumnsUpdate(null);
    handleSortChange(null);
  }, [handleFiltersUpdate, handleColumnsUpdate, handleSortChange]);

  const hasChanged = useMemo(() => {
    const hasFilterChanged =
      filtersMap[id]?.rules &&
      !isDeepEqual(
        getConditionIdentifiersOnly(filtersMap[id]),
        getConditionIdentifiersOnly(customView?.formConditions),
      );

    if (hasFilterChanged) {
      return true;
    }

    if (!customView) {
      return false;
    }

    const hasColumnsChanged =
      columnsMap[id] && !isDeepEqual(columnsMap[id], customView?.columns ?? []);

    if (hasColumnsChanged) {
      return true;
    }

    const viewSort = (customView?.sort ?? {}) as CustomViewSorting;

    const hasSortChanged =
      sortMap[id] &&
      (sortMap[id].attribute !== viewSort.attribute || sortMap[id].desc !== viewSort.desc);

    return hasSortChanged;
  }, [filtersMap, id, customView, columnsMap, sortMap]);

  return {
    filters,
    columns,
    sort,
    formMethods,
    formConditions,
    mappedConditions,
    queryParams,
    isQueryBuilderOpen,
    defaultPartnerFilter,
    setDefaultPartnerFilter,
    resetToDefault: hasChanged ? resetToDefault : undefined,
    handleFiltersUpdate,
    handleColumnUpdate,
    handleColumnsUpdate,
    handleSortChange,
    toggleQueryBuilder,
    setIsQueryBuilderOpen,
  };
};

function getConditionIdentifiersOnly(filters?: CustomViewFormModel['conditions']) {
  return filters?.rules?.map((i) => ({ ...i, field: { value: i.field?.value || '' } })) || [];
}
